/**
 * OCR Job Detail & Review Page
 *
 * Side-by-side review interface: image viewer left, form right.
 * Scientific precision aesthetic with instrument-style controls.
 */

import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ocrApi,
  type OcrJob,
  type OcrRecordsListResponse,
  type OcrCycleOption,
  type OcrImportPreview,
  type OcrImportResult,
  type OcrJobStats,
} from '@/lib/ocrApi';
import {
  ImageViewer,
  RecordReviewForm,
  ConfidenceIndicator,
  ConfidenceBar,
  CycleSelector,
  ImportSummary,
  ImportResultSummary,
} from '@/components/ocr';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Check,
  SkipForward,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { RoleGuard } from '@/components/layout/RoleGuard';

export const Route = createFileRoute('/_app/ocr/$jobId')({
  component: () => (
    <RoleGuard allowedRoles={['owner', 'supervisor', 'operator']}>
      <OcrJobDetailPage />
    </RoleGuard>
  ),
});

type RecordFilter = 'all' | 'needs_review' | 'reviewed' | 'skipped';
type OcrImage = { id: string; storage_path: string };

function buildPublicImageUrl(storagePath: string): string {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const encodedPath = storagePath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${baseUrl}/storage/v1/object/public/logbook-scans/${encodedPath}`;
}

function OcrJobDetailPage() {
  const { jobId } = Route.useParams() as { jobId: string };
  const queryClient = useQueryClient();

  // State
  const [currentRecordIndex, setCurrentRecordIndex] = useState(0);
  const [filter, setFilter] = useState<RecordFilter>('needs_review');
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, unknown> | null>(null);

  // Fetch job details
  const { data: job, isLoading: jobLoading } = useQuery({
    queryKey: ['ocr-job', jobId],
    queryFn: () => ocrApi.get<OcrJob>(`/ocr/jobs/${jobId}`),
    refetchInterval: (query) => query.state.data?.status === 'processing' ? 5000 : false,
  });

  // Fetch records
  const { data: recordsData } = useQuery({
    queryKey: ['ocr-records', jobId, filter],
    queryFn: () => {
      const params: Record<string, string> = { limit: '200' };
      if (filter === 'needs_review') params.needs_review = 'true';
      else if (filter === 'reviewed') params.needs_review = 'false';
      return ocrApi.get<OcrRecordsListResponse>(`/ocr/jobs/${jobId}/records`, { params });
    },
    enabled: !!job && job.status === 'complete',
  });

  // Fetch image storage paths for this job
  const { data: imagesData } = useQuery({
    queryKey: ['ocr-images', jobId],
    queryFn: () => ocrApi.get<OcrImage[]>(`/ocr/jobs/${jobId}/images`),
    enabled: !!job,
  });

  // Fetch cycles for assignment
  const { data: cyclesData } = useQuery({
    queryKey: ['ocr-cycles', jobId],
    queryFn: () => ocrApi.get<{ cycles: OcrCycleOption[] }>(`/ocr/jobs/${jobId}/cycles`),
    enabled: !!job,
  });

  // Fetch job stats
  const { data: stats } = useQuery({
    queryKey: ['ocr-job-stats', jobId],
    queryFn: () => ocrApi.get<OcrJobStats>(`/ocr/jobs/${jobId}/stats`),
    enabled: !!job && job.status === 'complete',
  });

  // Import preview
  const { data: importPreview } = useQuery({
    queryKey: ['ocr-import-preview', jobId],
    queryFn: () => ocrApi.get<OcrImportPreview>(`/ocr/jobs/${jobId}/import-preview`),
    enabled: showImportModal,
  });

  // Mutations
  const updateRecordMutation = useMutation({
    mutationFn: ({ recordId, data }: { recordId: string; data: Record<string, unknown> }) =>
      ocrApi.patch(`/ocr/jobs/${jobId}/records/${recordId}`, { corrected_data: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ocr-records', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ocr-job-stats', jobId] });
    },
  });

  const markReviewedMutation = useMutation({
    mutationFn: (recordId: string) =>
      ocrApi.post(`/ocr/jobs/${jobId}/records/${recordId}/reviewed`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ocr-records', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ocr-job-stats', jobId] });
      goToNextRecord();
    },
  });

  const skipRecordMutation = useMutation({
    mutationFn: ({ recordId, reason }: { recordId: string; reason?: string }) =>
      ocrApi.post(`/ocr/jobs/${jobId}/records/${recordId}/skip`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ocr-records', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ocr-job-stats', jobId] });
      goToNextRecord();
    },
  });

  const [importResult, setImportResult] = useState<OcrImportResult | null>(null);

  const importMutation = useMutation({
    mutationFn: () => ocrApi.post<OcrImportResult>(`/ocr/jobs/${jobId}/import`),
    onSuccess: (result) => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['ocr-records', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ocr-job-stats', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ocr-job', jobId] });
    },
  });

  // Derived data
  const records = useMemo(() => {
    if (!recordsData?.records) return [];
    if (filter === 'skipped') {
      return recordsData.records.filter(r => r.skipped);
    }
    return recordsData.records.filter(r => !r.skipped);
  }, [recordsData, filter]);

  const imagePathById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const image of imagesData || []) {
      map[image.id] = image.storage_path;
    }
    return map;
  }, [imagesData]);

  const currentRecord = records[currentRecordIndex];
  const currentRecordImagePath = currentRecord?.image_id
    ? imagePathById[currentRecord.image_id]
    : null;
  const cycles = cyclesData?.cycles || [];

  // Navigation
  const goToNextRecord = () => {
    if (currentRecordIndex < records.length - 1) {
      setCurrentRecordIndex(i => i + 1);
      setPendingChanges(null);
    }
  };

  const goToPrevRecord = () => {
    if (currentRecordIndex > 0) {
      setCurrentRecordIndex(i => i - 1);
      setPendingChanges(null);
    }
  };

  // Handlers
  const handleFormChange = (data: Record<string, unknown>) => {
    setPendingChanges(data);
  };

  const handleSaveAndNext = async () => {
    if (!currentRecord) return;

    if (pendingChanges) {
      await updateRecordMutation.mutateAsync({
        recordId: currentRecord.id,
        data: pendingChanges,
      });
    }
    markReviewedMutation.mutate(currentRecord.id);
  };

  const handleSkip = () => {
    if (!currentRecord) return;
    const reason = prompt('Reason for skipping (optional):');
    skipRecordMutation.mutate({ recordId: currentRecord.id, reason: reason || undefined });
  };

  // Loading state
  if (jobLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Job not found</h2>
        <Button
          variant="ghost"
          className="mt-4"
          onClick={() => window.history.back()}
        >
          Back to Jobs
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <header className="shrink-0 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Back
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Review Job #{jobId.slice(0, 8)}
              </h1>
              {stats && (
                <p className="text-xs text-gray-500">
                  {stats.reviewed_count} reviewed · {stats.pending_review} pending · {stats.imported_count} imported
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Filter */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {(['needs_review', 'reviewed', 'skipped'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => {
                    setFilter(f);
                    setCurrentRecordIndex(0);
                  }}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-md transition-all',
                    filter === f
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  {f === 'needs_review' ? 'Needs Review' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {/* Import button */}
            <Button
              variant="primary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={() => setShowImportModal(true)}
            >
              Import Ready
            </Button>
          </div>
        </div>
      </header>

      {/* Main content - Side by side */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - Image viewer */}
        <div className="w-1/2 border-r border-gray-200 bg-gray-900">
          {currentRecordImagePath ? (
            <ImageViewer
              src={buildPublicImageUrl(currentRecordImagePath)}
              alt="Logbook page"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <EyeOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No image available</p>
              </div>
            </div>
          )}
        </div>

        {/* Right panel - Review form */}
        <div className="w-1/2 flex flex-col bg-white">
          {records.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  All caught up!
                </h3>
                <p className="text-gray-500 text-sm">
                  {filter === 'needs_review'
                    ? 'No records need review.'
                    : `No ${filter} records.`}
                </p>
              </div>
            </div>
          ) : !currentRecord ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Record header */}
              <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      Record {currentRecordIndex + 1} of {records.length}
                    </span>
                    <ConfidenceIndicator
                      score={currentRecord.confidence_score}
                      showLabel
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={goToPrevRecord}
                      disabled={currentRecordIndex === 0}
                      className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={goToNextRecord}
                      disabled={currentRecordIndex >= records.length - 1}
                      className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <ConfidenceBar score={currentRecord.confidence_score} className="mt-2" />
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto">
                <RecordReviewForm
                  key={currentRecord.id}
                  record={currentRecord}
                  onChange={handleFormChange}
                />

                {/* Cycle selector */}
                {cycles.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Assign to Cycle
                    </label>
                    <CycleSelector
                      cycles={cycles}
                      value={
                        (pendingChanges?.cycle_id as string) ||
                        (currentRecord.corrected_data?.cycle_id as string) ||
                        (currentRecord.extracted_data?.cycle_id as string)
                      }
                      onChange={(cycleId) => {
                        setPendingChanges(prev => ({
                          ...(prev || currentRecord.corrected_data || currentRecord.extracted_data),
                          cycle_id: cycleId,
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    disabled={skipRecordMutation.isPending}
                    leftIcon={<SkipForward className="w-4 h-4" />}
                  >
                    Skip
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => markReviewedMutation.mutate(currentRecord.id)}
                      disabled={markReviewedMutation.isPending}
                      leftIcon={<Eye className="w-4 h-4" />}
                    >
                      Mark Reviewed
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSaveAndNext}
                      disabled={updateRecordMutation.isPending || markReviewedMutation.isPending}
                      loading={updateRecordMutation.isPending}
                      leftIcon={<Check className="w-4 h-4" />}
                    >
                      Save & Next
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Import Records
              </h2>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {importResult ? (
                <ImportResultSummary result={importResult} />
              ) : importPreview ? (
                <ImportSummary preview={importPreview} />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportResult(null);
                }}
              >
                {importResult ? 'Close' : 'Cancel'}
              </Button>
              {!importResult && (
                <Button
                  variant="primary"
                  onClick={() => importMutation.mutate()}
                  loading={importMutation.isPending}
                  disabled={!importPreview || importPreview.ready_to_import === 0}
                >
                  Import {importPreview?.ready_to_import || 0} Records
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
